import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

function cls(...a) {
  return a.filter(Boolean).join(' ');
}

function Modal({ open, onClose, title, children, widthClass = 'max-w-2xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-3" onMouseDown={onClose}>
      <div
        className={cls('w-full bg-white rounded-2xl shadow-xl border border-slate-200', widthClass)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-semibold text-slate-800">{title}</div>
          <button
            className="px-2 py-1 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <input
        type={type}
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pill({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };
  return (
    <span className={cls('inline-flex items-center px-2 py-0.5 rounded-full border text-xs', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function studentLabel(s) {
  const ln = (s.last_name || '').trim();
  const fn = (s.first_name || '').trim();
  const name = `${ln}${ln ? ', ' : ''}${fn}`.trim() || `Student #${s.id}`;
  return `${name} (${s.program_type || 'EI'})`;
}

export default function AdminInsurance() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');

  const [payers, setPayers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [coverages, setCoverages] = useState([]);

  // ---- NEW: students with coverage list ----
  const [coverageStudents, setCoverageStudents] = useState([]); // derived list
  const [coverageStudentsLoading, setCoverageStudentsLoading] = useState(false);
  const [coverageStudentsHint, setCoverageStudentsHint] = useState(''); // non-fatal hint msg

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // payer modal
  const [payerModal, setPayerModal] = useState(false);
  const [payerForm, setPayerForm] = useState({ id: null, name: '', payer_type: 'other', is_active: 1 });

  // plan modal
  const [planModal, setPlanModal] = useState(false);
  const [planForm, setPlanForm] = useState({ id: null, payer_id: '', name: '', payer_identifier: '', is_active: 1 });

  // coverage modal
  const [covModal, setCovModal] = useState(false);
  const [covForm, setCovForm] = useState({
    id: null,
    plan_id: '',
    member_id: '',
    group_id: '',
    effective_from: '',
    effective_to: '',
    is_primary: 1,
    status: 'active',
  });

  const studentOptions = useMemo(
    () =>
      students.map((s) => ({
        value: String(s.id),
        label: studentLabel(s),
      })),
    [students]
  );

  const payerOptions = useMemo(
    () => payers.map((p) => ({ value: String(p.id), label: `${p.name} (${p.payer_type})` })),
    [payers]
  );

  const planOptions = useMemo(() => {
    return plans.map((pl) => {
      const payer = payers.find((p) => String(p.id) === String(pl.payer_id));
      return { value: String(pl.id), label: `${pl.name}${payer ? ` — ${payer.name}` : ''}` };
    });
  }, [plans, payers]);

  // ---- NEW: load students who have coverage (fast) ----
  async function loadCoverageStudentsList() {
    setCoverageStudentsHint('');
    setCoverageStudentsLoading(true);
    try {
      // Expected BE route (org-protected):
      // GET /api/coverages/all -> returns rows with student_id, student_first_name, student_last_name, student_program_type,
      // payer_name, plan_name, member_id, group_id, is_primary, status, effective_from, effective_to, etc.
      const res = await API.get('/coverages/all');
      const rows = res.data || [];

      // group by student_id
      const byStudent = {};
      for (const r of rows) {
        const sid = Number(r.student_id);
        if (!sid) continue;
        if (!byStudent[sid]) byStudent[sid] = { student_id: sid, rows: [], student: null };
        byStudent[sid].rows.push(r);

        // try to build student object from row
        if (!byStudent[sid].student) {
          byStudent[sid].student = {
            id: sid,
            first_name: r.student_first_name || r.first_name || '',
            last_name: r.student_last_name || r.last_name || '',
            program_type: r.student_program_type || r.program_type || 'EI',
          };
        }
      }

      const list = Object.values(byStudent).map((x) => {
        const all = x.rows || [];
        const active = all.filter((c) => String(c.status || '') === 'active');
        const primaryActive =
          active.find((c) => Number(c.is_primary) === 1) ||
          all.find((c) => Number(c.is_primary) === 1) ||
          active[0] ||
          all[0] ||
          null;

        return {
          student_id: x.student_id,
          student_label: x.student ? studentLabel(x.student) : `Student #${x.student_id}`,
          coverages_count: all.length,
          active_count: active.length,
          primary: primaryActive
            ? {
                payer_name: primaryActive.payer_name,
                plan_name: primaryActive.plan_name,
                member_id: primaryActive.member_id,
                group_id: primaryActive.group_id,
                status: primaryActive.status,
                is_primary: primaryActive.is_primary,
                effective_from: primaryActive.effective_from,
                effective_to: primaryActive.effective_to,
              }
            : null,
        };
      });

      // sort by label
      list.sort((a, b) => String(a.student_label || '').localeCompare(String(b.student_label || '')));
      setCoverageStudents(list);
    } catch (e) {
      console.warn('Coverage students list unavailable:', e);
      setCoverageStudents([]);
      setCoverageStudentsHint('Lista zahteva BE rutu GET /api/coverages/all (org-protected). Trenutno nije dostupna.');
    } finally {
      setCoverageStudentsLoading(false);
    }
  }

  async function loadAll() {
    try {
      setErr('');
      setLoading(true);

      const [st, py, pl] = await Promise.all([
        API.get('/admin/students'), // već imaš ovu rutu u sistemu
        API.get('/payers'),
        API.get('/plans'),
      ]);

      setStudents(st.data || []);
      setPayers(py.data || []);
      setPlans(pl.data || []);

      // NEW: load students who have coverage
      await loadCoverageStudentsList();
    } catch (e) {
      console.error(e);
      setErr('Failed to load insurance data.');
    } finally {
      setLoading(false);
    }
  }

  async function loadCoverages(sid) {
    try {
      if (!sid) {
        setCoverages([]);
        return;
      }
      const res = await API.get(`/students/${sid}/coverages`);
      setCoverages(res.data || []);
    } catch (e) {
      console.error(e);
      setErr('Failed to load coverages.');
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    loadCoverages(studentId);
  }, [studentId]);

  function openNewPayer() {
    setPayerForm({ id: null, name: '', payer_type: 'other', is_active: 1 });
    setPayerModal(true);
  }
  function openEditPayer(p) {
    setPayerForm({ id: p.id, name: p.name, payer_type: p.payer_type, is_active: p.is_active ? 1 : 0 });
    setPayerModal(true);
  }

  function openNewPlan() {
    setPlanForm({ id: null, payer_id: payers[0]?.id ? String(payers[0].id) : '', name: '', payer_identifier: '', is_active: 1 });
    setPlanModal(true);
  }
  function openEditPlan(pl) {
    setPlanForm({
      id: pl.id,
      payer_id: String(pl.payer_id),
      name: pl.name,
      payer_identifier: pl.payer_identifier || '',
      is_active: pl.is_active ? 1 : 0,
    });
    setPlanModal(true);
  }

  function openNewCoverage() {
    if (!studentId) return;

    // Soft guard: warn if student already has coverage
    if (coverages && coverages.length) {
      const ok = window.confirm(
        'Coverage already exists.\n\nAdd another coverage (secondary)?'
      );
      if (!ok) return;
    }

    setCovForm({
      id: null,
      plan_id: plans[0]?.id ? String(plans[0].id) : '',
      member_id: '',
      group_id: '',
      effective_from: '',
      effective_to: '',
      is_primary: 1,
      status: 'active',
    });
    setCovModal(true);
  }

  function openEditCoverage(c) {
    setCovForm({
      id: c.id,
      plan_id: String(c.plan_id),
      member_id: c.member_id || '',
      group_id: c.group_id || '',
      effective_from: (c.effective_from || '').slice(0, 10),
      effective_to: (c.effective_to || '').slice(0, 10),
      is_primary: c.is_primary ? 1 : 0,
      status: c.status || 'active',
    });
    setCovModal(true);
  }

  async function savePayer() {
    try {
      setErr('');
      const payload = {
        name: payerForm.name,
        payer_type: payerForm.payer_type,
        is_active: payerForm.is_active ? 1 : 0,
      };
      if (payerForm.id) await API.put(`/payers/${payerForm.id}`, payload);
      else await API.post('/payers', payload);

      setPayerModal(false);
      await loadAll();
    } catch (e) {
      console.error(e);
      setErr('Failed to save payer.');
    }
  }

  async function deletePayer(id) {
    if (!window.confirm('Delete payer? This may delete plans too.')) return;
    try {
      setErr('');
      await API.delete(`/payers/${id}`);
      await loadAll();
    } catch (e) {
      console.error(e);
      setErr('Failed to delete payer.');
    }
  }

  async function savePlan() {
    try {
      setErr('');
      const payload = {
        payer_id: Number(planForm.payer_id),
        name: planForm.name,
        payer_identifier: planForm.payer_identifier || null,
        is_active: planForm.is_active ? 1 : 0,
      };
      if (planForm.id) await API.put(`/plans/${planForm.id}`, payload);
      else await API.post('/plans', payload);

      setPlanModal(false);
      await loadAll();
    } catch (e) {
      console.error(e);
      setErr('Failed to save plan.');
    }
  }

  async function deletePlan(id) {
    if (!window.confirm('Delete plan?')) return;
    try {
      setErr('');
      await API.delete(`/plans/${id}`);
      await loadAll();
      await loadCoverages(studentId);
    } catch (e) {
      console.error(e);
      setErr('Failed to delete plan.');
    }
  }

  async function saveCoverage() {
    try {
      setErr('');
      if (!studentId) return setErr('Select student first.');
      const payload = {
        plan_id: Number(covForm.plan_id),
        member_id: covForm.member_id,
        group_id: covForm.group_id || null,
        effective_from: covForm.effective_from || null,
        effective_to: covForm.effective_to || null,
        is_primary: covForm.is_primary ? 1 : 0,
        status: covForm.status,
      };

      if (covForm.id) {
        await API.put(`/coverages/${covForm.id}`, payload);
      } else {
        await API.post(`/students/${studentId}/coverages`, payload);
      }

      setCovModal(false);
      await loadCoverages(studentId);
      await loadCoverageStudentsList(); // refresh list below
    } catch (e) {
      console.error(e);
      setErr('Failed to save coverage.');
    }
  }

  async function deleteCoverage(id) {
    if (!window.confirm('Delete coverage?')) return;
    try {
      setErr('');
      await API.delete(`/coverages/${id}`);
      await loadCoverages(studentId);
      await loadCoverageStudentsList(); // refresh list below
    } catch (e) {
      console.error(e);
      setErr('Failed to delete coverage.');
    }
  }

  return (
    <div className="p-3 md:p-6">
      <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-indigo-50 border border-slate-200 shadow-sm p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-lg md:text-xl font-semibold text-slate-900">Insurance (Payers, Plans, Coverages)</div>
            
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={openNewPayer} className="px-3 py-2 rounded-xl bg-sky-600 text-white text-sm hover:bg-sky-700">
              + Add Payer
            </button>
            <button onClick={openNewPlan} className="px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm hover:bg-indigo-700">
              + Add Plan
            </button>
            <button onClick={() => loadAll()} className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50">
              Refresh
            </button>
          </div>
        </div>

        {err ? (
          <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div>
        ) : null}

        {loading ? (
          <div className="mt-6 text-slate-600 text-sm">Loading…</div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* PAYERS */}
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-800">Payers</div>
                  <Pill tone="sky">{payers.length}</Pill>
                </div>
                <div className="mt-3 space-y-2">
                  {payers.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {p.payer_type} • {p.is_active ? 'active' : 'inactive'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="px-2 py-1 text-xs rounded-lg border border-slate-200 hover:bg-slate-50"
                          onClick={() => openEditPayer(p)}
                        >
                          Edit
                        </button>
                        <button
                          className="px-2 py-1 text-xs rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                          onClick={() => deletePayer(p.id)}
                        >
                          Del
                        </button>
                      </div>
                    </div>
                  ))}
                  {!payers.length ? <div className="text-sm text-slate-600">No payers yet.</div> : null}
                </div>
              </div>

              {/* PLANS */}
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-800">Plans</div>
                  <Pill tone="indigo">{plans.length}</Pill>
                </div>
                <div className="mt-3 space-y-2">
                  {plans.map((pl) => {
                    const payer = payers.find((p) => String(p.id) === String(pl.payer_id));
                    return (
                      <div key={pl.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{pl.name}</div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            {payer ? payer.name : '—'} • {pl.payer_identifier ? `id:${pl.payer_identifier}` : 'no id'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="px-2 py-1 text-xs rounded-lg border border-slate-200 hover:bg-slate-50"
                            onClick={() => openEditPlan(pl)}
                          >
                            Edit
                          </button>
                          <button
                            className="px-2 py-1 text-xs rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                            onClick={() => deletePlan(pl.id)}
                          >
                            Del
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!plans.length ? <div className="text-sm text-slate-600">No plans yet.</div> : null}
                </div>
              </div>

              {/* COVERAGES */}
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-800">Student Coverages</div>
                  <div className="flex items-center gap-2">
                    <Pill tone="emerald">{coverages.length}</Pill>
                    <button
                      disabled={!studentId}
                      onClick={openNewCoverage}
                      className={cls(
                        'px-2.5 py-1.5 rounded-lg text-xs border',
                        studentId
                          ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                          : 'bg-slate-100 text-slate-400 border-slate-200'
                      )}
                    >
                      + Add Coverage
                    </button>
                  </div>
                </div>

                <div className="mt-3">
                  <Select label="Select student" value={studentId} onChange={setStudentId} options={studentOptions} />
                </div>

                <div className="mt-3 space-y-2">
                  {coverages.map((c) => (
                    <div key={c.id} className="rounded-xl border border-slate-200 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {c.payer_name} • {c.plan_name}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            member: <span className="font-mono">{c.member_id}</span>
                            {c.group_id ? (
                              <>
                                {' '}
                                • group: <span className="font-mono">{c.group_id}</span>
                              </>
                            ) : null}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            {c.effective_from ? `from ${String(c.effective_from).slice(0, 10)}` : 'from —'} •{' '}
                            {c.effective_to ? `to ${String(c.effective_to).slice(0, 10)}` : 'to —'} •{' '}
                            {c.is_primary ? <Pill tone="sky">PRIMARY</Pill> : <Pill>secondary</Pill>} •{' '}
                            {c.status === 'active' ? <Pill tone="emerald">active</Pill> : <Pill tone="amber">inactive</Pill>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            className="px-2 py-1 text-xs rounded-lg border border-slate-200 hover:bg-slate-50"
                            onClick={() => openEditCoverage(c)}
                          >
                            Edit
                          </button>
                          <button
                            className="px-2 py-1 text-xs rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                            onClick={() => deleteCoverage(c.id)}
                          >
                            Del
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {studentId && !coverages.length ? (
                    <div className="text-sm text-slate-600">No coverages for this student yet.</div>
                  ) : null}
                  {!studentId ? <div className="text-sm text-slate-600">Select a student to manage coverages.</div> : null}
                </div>
              </div>
            </div>

            {/* NEW: Students who have coverage list (below the 3 blocks) */}
            <div className="mt-5 rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-800">Students with Coverage</div>
                <div className="flex items-center gap-2">
                  <Pill tone="indigo">{coverageStudents.length}</Pill>
                  <button
                    onClick={loadCoverageStudentsList}
                    className="px-2.5 py-1.5 rounded-lg text-xs border border-slate-200 bg-white hover:bg-slate-50"
                  >
                    Refresh list
                  </button>
                </div>
              </div>

              {coverageStudentsHint ? (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm">
                  {coverageStudentsHint}
                </div>
              ) : null}

              {coverageStudentsLoading ? (
                <div className="mt-3 text-sm text-slate-600">Loading list…</div>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-600 border-b border-slate-200">
                        <th className="text-left py-2 pr-3">Student</th>
                        <th className="text-left py-2 pr-3">Counts</th>
                        <th className="text-left py-2 pr-3">Primary (quick view)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverageStudents.map((r) => {
                        const p = r.primary;
                        return (
                          <tr key={r.student_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 pr-3">
                              <div className="font-medium text-slate-900">{r.student_label}</div>
                            </td>
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Pill tone="slate">{r.coverages_count} total</Pill>
                                <Pill tone={r.active_count ? 'emerald' : 'amber'}>{r.active_count} active</Pill>
                              </div>
                            </td>
                            <td className="py-2 pr-3">
                              {p ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Pill tone="sky">{p.payer_name || '—'}</Pill>
                                  <Pill tone="indigo">{p.plan_name || '—'}</Pill>
                                  <Pill tone={String(p.status) === 'active' ? 'emerald' : 'amber'}>{p.status || '—'}</Pill>
                                  {Number(p.is_primary) === 1 ? <Pill tone="sky">PRIMARY</Pill> : <Pill>secondary</Pill>}
                                  <span className="text-xs text-slate-600">
                                    member: <span className="font-mono">{p.member_id || '—'}</span>
                                  </span>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-600">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {!coverageStudents.length && !coverageStudentsHint ? (
                        <tr>
                          <td className="py-3 text-slate-600" colSpan={3}>
                            No students with coverage yet.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* PAYER MODAL */}
      <Modal open={payerModal} onClose={() => setPayerModal(false)} title={payerForm.id ? 'Edit Payer' : 'Add Payer'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Payer name" value={payerForm.name} onChange={(v) => setPayerForm((p) => ({ ...p, name: v }))} />
          <Select
            label="Type"
            value={payerForm.payer_type}
            onChange={(v) => setPayerForm((p) => ({ ...p, payer_type: v }))}
            options={[
              { value: 'medicaid', label: 'medicaid' },
              { value: 'commercial', label: 'commercial' },
              { value: 'other', label: 'other' },
            ]}
          />
          <Select
            label="Active"
            value={String(payerForm.is_active)}
            onChange={(v) => setPayerForm((p) => ({ ...p, is_active: v === '1' ? 1 : 0 }))}
            options={[
              { value: '1', label: 'Yes' },
              { value: '0', label: 'No' },
            ]}
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setPayerModal(false)}
            className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button onClick={savePayer} className="px-3 py-2 rounded-xl bg-sky-600 text-white text-sm hover:bg-sky-700">
            Save
          </button>
        </div>
      </Modal>

      {/* PLAN MODAL */}
      <Modal open={planModal} onClose={() => setPlanModal(false)} title={planForm.id ? 'Edit Plan' : 'Add Plan'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select label="Payer" value={planForm.payer_id} onChange={(v) => setPlanForm((p) => ({ ...p, payer_id: v }))} options={payerOptions} />
          <Input label="Plan name" value={planForm.name} onChange={(v) => setPlanForm((p) => ({ ...p, name: v }))} />
          <Input
            label="Plan identifier (optional)"
            value={planForm.payer_identifier}
            onChange={(v) => setPlanForm((p) => ({ ...p, payer_identifier: v }))}
            placeholder="e.g. payer code"
          />
          <Select
            label="Active"
            value={String(planForm.is_active)}
            onChange={(v) => setPlanForm((p) => ({ ...p, is_active: v === '1' ? 1 : 0 }))}
            options={[
              { value: '1', label: 'Yes' },
              { value: '0', label: 'No' },
            ]}
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setPlanModal(false)}
            className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button onClick={savePlan} className="px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm hover:bg-indigo-700">
            Save
          </button>
        </div>
      </Modal>

      {/* COVERAGE MODAL */}
      <Modal open={covModal} onClose={() => setCovModal(false)} title={covForm.id ? 'Edit Coverage' : 'Add Coverage'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select label="Plan" value={covForm.plan_id} onChange={(v) => setCovForm((p) => ({ ...p, plan_id: v }))} options={planOptions} />
          <Input label="Member ID" value={covForm.member_id} onChange={(v) => setCovForm((p) => ({ ...p, member_id: v }))} />
          <Input label="Group ID (optional)" value={covForm.group_id} onChange={(v) => setCovForm((p) => ({ ...p, group_id: v }))} />
          <Input label="Effective from" type="date" value={covForm.effective_from} onChange={(v) => setCovForm((p) => ({ ...p, effective_from: v }))} />
          <Input label="Effective to" type="date" value={covForm.effective_to} onChange={(v) => setCovForm((p) => ({ ...p, effective_to: v }))} />
          <Select
            label="Primary"
            value={String(covForm.is_primary)}
            onChange={(v) => setCovForm((p) => ({ ...p, is_primary: v === '1' ? 1 : 0 }))}
            options={[
              { value: '1', label: 'Yes' },
              { value: '0', label: 'No' },
            ]}
          />
          <Select
            label="Status"
            value={covForm.status}
            onChange={(v) => setCovForm((p) => ({ ...p, status: v }))}
            options={[
              { value: 'active', label: 'active' },
              { value: 'inactive', label: 'inactive' },
            ]}
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setCovModal(false)}
            className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button onClick={saveCoverage} className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm hover:bg-emerald-700">
            Save
          </button>
        </div>
      </Modal>
    </div>
  );
}


